// Fin de service — overlay MAISON en 2 temps : confirmation (compte des ouvertes
// via wrapper, INV-C, ENRICHIE LOT 7 §5 d'un récapitulatif des commandes encore
// actives — contrôle global avant clôture) puis bilan plein écran statique
// (faits bruts, chiffres en JetBrains Mono, zéro smiley/auto-évaluation, aucun
// email — D4). Sans session active : titre « Bilan du jour » (§5.3).
// Emplacement UNIQUE (LOT 7 §5) : bas de l'écran Prise de commande. Zéro animation.
import { useState } from 'react';
import { getDb } from '@/db';
import { withTenant } from '@/db/tenant';
import { endServiceT1, type EndServiceT1Result } from '@/db/transactions';
import { useFileCommandes } from '@/hooks/useFileCommandes';
import { useNumerosService } from '@/hooks/useNumerosService';
import { LIBELLE_STATUT } from '@/hooks/useT1ActionGate';
import { nomAffichage } from '@/lib/affichage-commande';
import { formatDateFr, formatDuree, formatHeure } from '@/lib/format';
import { pluriel } from '@/lib/pluriel';

export function FinDeServiceOverlay({ onClose }: { onClose: () => void }) {
  const { commandes, lignesParCommande } = useFileCommandes();
  const numeros = useNumerosService();
  const [bilan, setBilan] = useState<EndServiceT1Result | null>(null);
  const [titre, setTitre] = useState('SERVICE TERMINÉ');

  const confirmer = async (): Promise<void> => {
    // Titre selon session active (déterminé avant clôture — §5.3).
    const sessions = await withTenant(getDb().sessions).toArray();
    const active = sessions.some((s) => s.endedAt === undefined);
    setTitre(active ? 'SERVICE TERMINÉ' : 'BILAN DU JOUR');
    setBilan(await endServiceT1());
  };

  if (bilan === null) {
    return (
      <div
        data-testid="finservice-confirm"
        className="fixed inset-0 z-50 flex items-center justify-center bg-ardoise/40 p-6"
      >
        <div className="flex max-h-[90vh] w-full max-w-2xl flex-col gap-6 overflow-auto rounded-xl border-4 border-ardoise bg-creme p-8">
        <p className="font-cormorant text-4xl italic text-ardoise">Terminer le service ?</p>
        <p className="font-jost text-2xl text-ardoise">
          <span className="font-mono" data-testid="ouvertes-count">
            {commandes.length}
          </span>{' '}
          {pluriel(commandes.length, 'commande')} encore{' '}
          {pluriel(commandes.length, 'ouverte')}
        </p>
        {/* Récapitulatif des commandes encore actives (LOT 7 §5) — contrôle
            global Prise de commande + Production avant clôture. */}
        {commandes.length > 0 && (
          <ul
            data-testid="finservice-recap"
            className="flex max-h-[40vh] flex-col gap-2 overflow-auto"
          >
            {commandes.map((c) => {
              const nb = (lignesParCommande.get(c.id as number) ?? []).reduce(
                (s, l) => s + l.qty,
                0,
              );
              return (
                <li key={c.id} className="font-jost text-xl text-ardoise">
                  <span
                    className={
                      c.client_nom_snapshot ? 'font-cormorant text-2xl italic' : ''
                    }
                  >
                    {nomAffichage(c, numeros)}
                  </span>{' '}
                  ·{' '}
                  <span className="font-mono">{nb}</span>{' '}
                  {pluriel(nb, 'pizza')} · {LIBELLE_STATUT[c.statut]} ·{' '}
                  <span className="font-mono">
                    {formatHeure(c.heure_retrait_proposee)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
        <p className="font-jost text-lg text-muted-foreground">
          prêtes → clôturées · autres → annulées
        </p>
        <div className="flex gap-4">
          <button
            type="button"
            onClick={() => void confirmer()}
            className="min-h-[120px] flex-1 rounded-lg bg-ardoise font-jost text-2xl text-creme"
          >
            CONFIRMER
          </button>
          <button
            type="button"
            onClick={onClose}
            className="min-h-[120px] flex-1 rounded-lg border-2 border-ardoise bg-creme font-jost text-2xl text-ardoise"
          >
            ANNULER
          </button>
        </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="finservice-bilan"
      className="fixed inset-0 z-50 flex flex-col gap-5 bg-creme p-8"
    >
      <h2 className="font-cormorant text-4xl italic text-ardoise">
        {titre} — <span className="font-mono">{formatDateFr(bilan.fenetre.fin)}</span>
      </h2>
      <p className="font-mono text-xl text-ardoise">
        {formatHeure(bilan.fenetre.debut)} → {formatHeure(bilan.fenetre.fin)} ·{' '}
        {formatDuree(bilan.duree)}
      </p>
      <dl className="flex flex-col gap-3 font-jost text-2xl text-ardoise">
        <div className="flex justify-between gap-6">
          <dt>PIZZAS VENDUES</dt>
          <dd className="font-mono" data-testid="bilan-vendues">
            {bilan.totaux.pizzasVendues}
          </dd>
        </div>
        <div className="flex justify-between gap-6">
          <dt>Pizzas refaites</dt>
          <dd className="font-mono" data-testid="bilan-refaites">
            {bilan.totaux.pizzasRefaites}
          </dd>
        </div>
        <div className="flex justify-between gap-6">
          <dt>Commandes annulées</dt>
          <dd className="font-mono" data-testid="bilan-annulees">
            {bilan.totaux.commandesAnnulees}
          </dd>
        </div>
        <div className="flex justify-between gap-6">
          <dt>TOTAL commandes</dt>
          <dd className="font-mono" data-testid="bilan-total">
            {bilan.totaux.commandes}
          </dd>
        </div>
      </dl>
      <button
        type="button"
        onClick={onClose}
        className="min-h-[80px] rounded-lg bg-ardoise font-jost text-2xl text-creme"
      >
        FERMER
      </button>
    </div>
  );
}
